import { useState } from "react";

import { DRIVE_CONSTANTS } from "@/constants/Drive.constant";
import { driveTypes } from "@/constants/constant-values/default-values.constant";
import { REQUEST_METHODS } from "@/constants/global-constants";
import { useAlterLeadsDriveMutation } from "@/redux/api/prQuery";
import { useAlterProjectMutation } from "@/redux/api/projectsQuery";
import { customToast } from "@/utils/custom-toast";
import { validateResponse } from "@/utils/helper-functions/form.helper";

// Define item type that can be moved
export type DriveItemToMove = {
  id: string;
  type: "file" | "directory";
  name?: string;
};

// Define drive type
export type TDriveType = (typeof driveTypes)[keyof typeof driveTypes];

// Options for move operation
export interface MoveOptions {
  item: DriveItemToMove;
  driveType: TDriveType;
  sourceItemId: string;
  sourceDirectoryId?: string;
  targetItemId?: string;
}

// Request structure for API
export interface MoveDriveItemRequest {
  sourceItemId: string;
  sourceItemType: "file" | "directory";
  targetItemId?: string;
  driveType: TDriveType;
}

// Result of a move operation
export interface MoveResult {
  success: boolean;
  message?: string;
  wasCancelled?: boolean;
}

export const useDriveMove = (): {
  isMoving: boolean;
  isCancelled: boolean;
  moveDriveItem: (options: MoveOptions) => Promise<MoveResult>;
  cancelMove: () => void;
  resetMoveState: () => void;
} => {
  const [isMoving, setIsMoving] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);
  const [moveAbortController, setMoveAbortController] =
    useState<AbortController | null>(null);
  const [alterProject] = useAlterProjectMutation();
  const [alterLeadsDrive] = useAlterLeadsDriveMutation();

  const getMutationHandler = (
    driveType: TDriveType,
  ): typeof alterProject | typeof alterLeadsDrive => {
    return driveType === driveTypes.PROJECT_REGISTER
      ? alterLeadsDrive
      : alterProject;
  };

  // Reset all state variables related to the move operation
  const resetMoveState = (): void => {
    setIsMoving(false);
    setIsCancelled(false);
    setMoveAbortController(null);
  };

  // Cancel an ongoing move operation
  const cancelMove = (): void => {
    if (isMoving && moveAbortController) {
      moveAbortController.abort();
      setIsCancelled(true);
      setIsMoving(false);
      customToast.info("Move operation cancelled");
    }
  };

  const moveDriveItem = async (options: MoveOptions): Promise<MoveResult> => {
    // Reset cancelled state at the start of a new operation
    setIsCancelled(false);
    setIsMoving(true);
    // Create abort controller for cancellation
    const abortController = new AbortController();
    setMoveAbortController(abortController);

    try {
      const { item, driveType, sourceItemId, sourceDirectoryId, targetItemId } =
        options;
      // Don't allow moving to the same folder
      if (sourceDirectoryId === targetItemId) {
        setIsMoving(false);
        throw new Error(DRIVE_CONSTANTS.MESSAGES.CANT_MOVE_ON_SAME_FOLDER);
      }

      // Prepare request body
      const requestBody: MoveDriveItemRequest = {
        sourceItemId: item.id,
        sourceItemType: item.type,
        targetItemId,
        driveType,
      };
      // Get appropriate mutation handler
      const mutationHandler = getMutationHandler(driveType);

      // Determine the parameter key based on drive type
      const paramKey =
        driveType === driveTypes.PROJECT_REGISTER ? "leadsId" : "projectId";

      // Make API request with abort signal if supported
      const response = await mutationHandler({
        [paramKey]: sourceItemId,
        data: requestBody,
        method: REQUEST_METHODS.POST,
        params: `drive/move`,
      });

      // Check if operation was cancelled during the API call
      if (isCancelled) {
        return {
          success: false,
          wasCancelled: true,
          message: "Move operation was cancelled",
        };
      }

      // Handle success using validateResponse
      let success = false;
      validateResponse({
        response,
        onSuccess: () => {
          const successMessage =
            DRIVE_CONSTANTS.MESSAGES.MOVE_SUCCESS ||
            `${item.type === "directory" ? "Folder" : "File"} moved successfully`;
          customToast.success(successMessage);
          success = true;
        },
        onError: () => {
          throw new Error("Failed to move item");
        },
      });

      return {
        success,
        message: success
          ? DRIVE_CONSTANTS.MESSAGES.MOVE_SUCCESS ||
            `${item.type === "directory" ? "Folder" : "File"} moved successfully`
          : undefined,
      };
    } catch (error) {
      // Check if operation was aborted/cancelled
      if (error instanceof DOMException && error.name === "AbortError") {
        return {
          success: false,
          wasCancelled: true,
          message: "Move operation was cancelled",
        };
      }

      // Check if operation was already marked as cancelled
      if (isCancelled) {
        return {
          success: false,
          wasCancelled: true,
          message: "Move operation was cancelled",
        };
      }

      // Handle other errors
      const errorMessage =
        error instanceof Error
          ? error.message
          : DRIVE_CONSTANTS.MESSAGES.MOVE_ERROR || "Failed to move item";

      customToast.error(errorMessage);

      return { success: false, message: errorMessage };
    } finally {
      // Always reset moving state unless explicitly cancelled
      if (!isCancelled) {
        setIsMoving(false);
      }

      // Clear the abort controller reference
      setMoveAbortController(null);
    }
  };

  return {
    isMoving,
    isCancelled,
    moveDriveItem,
    cancelMove,
    resetMoveState,
  };
};

export default useDriveMove;
